import { AnimatePresence, motion } from 'motion/react'
import { useEffect } from 'react'
import { useI18n } from '../i18n/useI18n'
import { ITEMS, RARITY_COLOR } from '../game/items'
import type { Toast } from '../game/types'
import { useGameStore } from '../store/useGameStore'

function ToastItem({
  toast: toastData,
  onDismiss,
}: {
  toast: Toast
  onDismiss: () => void
}) {
  const { t, pick } = useI18n()
  useEffect(() => {
    const id = window.setTimeout(onDismiss, 4500)
    return () => window.clearTimeout(id)
  }, [onDismiss])

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -16, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.95 }}
      transition={{ type: 'spring', stiffness: 380, damping: 30 }}
      className="flex items-start gap-2 rounded-xl border border-stone-700 bg-stone-800/95 px-3 py-2.5 shadow-xl backdrop-blur"
    >
      <div className="pt-0.5 text-base">🧭</div>
      <div className="min-w-0 flex-1">
        <div className="text-xs font-semibold text-stone-100">
          {toastData.message
            ? pick(toastData.message)
            : t('toast_back', { name: toastData.survivorName })}
        </div>
        {toastData.delivered.length > 0 ? (
          <div className="mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
            {toastData.delivered.map((stack) => {
              const def = ITEMS[stack.defId]
              if (!def) return null
              return (
                <span
                  key={stack.defId}
                  className={`text-[11px] ${RARITY_COLOR[def.rarity]}`}
                >
                  +{stack.quantity} {pick(def.name)}
                </span>
              )
            })}
          </div>
        ) : toastData.message ? null : (
          <div className="mt-0.5 text-[11px] italic text-stone-500">
            {t('toast_nothing')}
          </div>
        )}
        {toastData.removed && toastData.removed.length > 0 && (
          <div className="mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
            {toastData.removed.map((stack) => {
              const def = ITEMS[stack.defId]
              if (!def) return null
              return (
                <span key={stack.defId} className="text-[11px] text-red-400">
                  −{stack.quantity} {pick(def.name)}
                </span>
              )
            })}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="shrink-0 text-stone-600 hover:text-stone-400"
      >
        ✕
      </button>
    </motion.div>
  )
}

export function ToastStack() {
  const toasts = useGameStore((s) => s.toasts)
  const dismissToast = useGameStore((s) => s.dismissToast)

  return (
    <div className="pointer-events-none fixed left-1/2 top-14 z-40 flex w-full max-w-[400px] -translate-x-1/2 flex-col gap-2 px-3">
      <AnimatePresence mode="popLayout">
        {toasts.map((toast) => (
          <div key={toast.id} className="pointer-events-auto">
            <ToastItem
              toast={toast}
              onDismiss={() => dismissToast(toast.id)}
            />
          </div>
        ))}
      </AnimatePresence>
    </div>
  )
}
